//import '../styles/TableViewer.css';
import '../styles/ModernTable.css';
import React, {useEffect, useState} from "react";
import * as XLSX from "xlsx";

export default function DynamicTable({data, onAction}) {
    const [search, setSearch] = useState("");
    const [filtered, setFiltered] = useState(data);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const q = search.toLowerCase();
        const result = data.filter((s) =>
            Object.values(s).some((val) => {
                if (typeof val === "string") return val.toLowerCase().includes(q);
                if (typeof val === "object" && val !== null)
                    return Object.values(val).some(
                        (nested) =>
                            typeof nested === "string" &&
                            nested.toLowerCase().includes(q)
                    );
                return false;
            })
        );
        setFiltered(result);
    }, [search, data]);

    // Sayfalama için hesaplamalar
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = filtered.slice(startIndex, endIndex);

    // Sıralama fonksiyonu
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });

        const sortedData = [...filtered].sort((a, b) => {
            let aVal = a[key];
            let bVal = b[key];

            // Object kontrolü
            if (typeof aVal === "object" && aVal !== null) {
                aVal = aVal.groupName || aVal.name || aVal.id || "";
            }
            if (typeof bVal === "object" && bVal !== null) {
                bVal = bVal.groupName || bVal.name || bVal.id || "";
            }

            // String'e çevir
            aVal = String(aVal || "").toLowerCase();
            bVal = String(bVal || "").toLowerCase();

            if (direction === 'asc') {
                return aVal.localeCompare(bVal, 'tr');
            } else {
                return bVal.localeCompare(aVal, 'tr');
            }
        });

        setFiltered(sortedData);
        setCurrentPage(1); // Sıralama sonrası ilk sayfaya git
    };

    // Sayfa değiştirme
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Export fonksiyonları
    const exportToCSV = () => {
        setLoading(true);
        try {
            const csv = [
                columns.join(","),
                ...filtered.map(row =>
                    columns.map(col => {
                        let value = row[col];
                        if (typeof value === "object" && value !== null) {
                            value = value.groupName || value.name || value.id || "";
                        }
                        return JSON.stringify(value ?? "");
                    }).join(",")
                )
            ].join("\n");

            const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `export_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('CSV Export Error:', error);
            alert('CSV export sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        setLoading(true);
        try {
            const processedData = filtered.map(row => {
                const newRow = {};
                columns.forEach(col => {
                    let value = row[col];
                    if (typeof value === "object" && value !== null) {
                        value = value.groupName || value.name || value.id || "";
                    }
                    newRow[col] = value ?? "";
                });
                return newRow;
            });

            const worksheet = XLSX.utils.json_to_sheet(processedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Veriler");

            XLSX.writeFile(workbook, `export_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Excel Export Error:', error);
            alert('Excel export sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Türkçe sütun adları
    const getColumnDisplayName = (col) => {
        const columnNames = {
            'id': 'ID',
            'name': 'Ad',
            'email': 'E-posta',
            'phone': 'Telefon',
            'firstName': 'Ad',
            'lastName': 'Soyad',
            'fullName': 'Ad Soyad',
            'courseId': 'Kurs ID',
            'courseName': 'Kurs Adı',
            'enabled': 'Aktif',
            'createdAt': 'Oluşturma Tarihi',
            'updatedAt': 'Güncelleme Tarihi',
            'birthday': 'Doğum Tarihi',
            'city': 'Şehir',
            'profession': 'Meslek',
            'educationBackground': 'Eğitim Durumu',
            'monthlyIncome': 'Aylık Gelir',
            'vrExperience': 'VR Deneyimi',
            'hasVrDevice': 'VR Cihazı',
            'professionalLevel': 'Profesyonel Seviye',
            'motivation': 'Motivasyon'
        };
        return columnNames[col] || col.charAt(0).toUpperCase() + col.slice(1);
    };

    // Değer formatlama
    const formatCellValue = (value, column) => {
        if (value === null || value === undefined || value === '') return '—';

        if (typeof value === "object" && value !== null) {
            return value.groupName || value.name || value.id || '—';
        }

        // Boolean değerler için
        if (typeof value === 'boolean') {
            return value ? '✅ Evet' : '❌ Hayır';
        }

        // Tarih formatı
        if (column.includes('date') || column.includes('Date')) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('tr-TR');
                }
            } catch (e) {
                // Ignore error
            }
        }

        // Email formatı
        if (column === 'email' && typeof value === 'string' && value.includes('@')) {
            return (
                <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800">
                    {value}
                </a>
            );
        }

        return String(value);
    };

    // Aksiyon sütunu var mı kontrolü
    const hasActions = onAction && data.length > 0 && data[0].hasOwnProperty('enabled');

    if (!data || data.length === 0) {
        return (
            <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>Veri Bulunamadı</h3>
                <p>Gösterilecek herhangi bir veri bulunmuyor.</p>
            </div>
        );
    }

    if (!Array.isArray(data) || data.length === 0) {
        return (
            <div className="empty-state">
                <div className="empty-icon">⚠️</div>
                <h3>Veri Hatası</h3>
                <p>Veri formatı geçersiz veya tablo boş.</p>
            </div>
        );
    }

    const columns = Object.keys(data[0]);

    return (
        <div className="modern-table-container">
            {/* Header Section */}
            <div className="table-header">
                <div className="table-controls-left">
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                placeholder="Tabloda ara..."
                                className="search-input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && (
                                <button
                                    className="clear-search"
                                    onClick={() => setSearch('')}
                                    title="Aramayı temizle"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="items-per-page">
                        <label>Sayfa başına:</label>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="per-page-select"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>

                <div className="table-controls-right">
                    <div className="table-info">
                        <span className="results-count">
                            {filtered.length} sonuç • Sayfa {currentPage}/{totalPages}
                        </span>
                    </div>

                    <div className="export-buttons">
                        <button
                            onClick={exportToCSV}
                            className="export-btn csv-btn"
                            disabled={loading}
                            title="CSV olarak dışa aktar"
                        >
                            {loading ? '⏳' : '📄'} CSV
                        </button>
                        <button
                            onClick={exportToExcel}
                            className="export-btn excel-btn"
                            disabled={loading}
                            title="Excel olarak dışa aktar"
                        >
                            {loading ? '⏳' : '📊'} Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="table-wrapper">
                <table className="modern-table">
                    <thead>
                        <tr>
                            <th className="row-number-header">#</th>
                            {columns.map((col, i) => (
                                <th
                                    key={i}
                                    className={`sortable-header ${sortConfig.key === col ? 'sorted' : ''}`}
                                    onClick={() => handleSort(col)}
                                    title={`${getColumnDisplayName(col)} sütununa göre sırala`}
                                >
                                    <div className="header-content">
                                        <span>{getColumnDisplayName(col)}</span>
                                        <span className="sort-icon">
                                            {sortConfig.key === col ? (
                                                sortConfig.direction === 'asc' ? '↑' : '↓'
                                            ) : '↕️'}
                                        </span>
                                    </div>
                                </th>
                            ))}
                            {hasActions && (
                                <th className="actions-header">
                                    İşlemler
                                </th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {currentData.map((row, i) => (
                            <tr key={i} className="table-row">
                                <td className="row-number">{startIndex + i + 1}</td>
                                {columns.map((col, j) => (
                                    <td key={j} className="table-cell">
                                        {formatCellValue(row[col], col)}
                                    </td>
                                ))}
                                {hasActions && (
                                    <td className="actions-cell">
                                        {row.enabled === false ? (
                                            <button
                                                className="action-btn approve-btn"
                                                onClick={() => onAction('enable', row)}
                                                title="Katılımcıyı onayla"
                                            >
                                                ✅ Onayla
                                            </button>
                                        ) : row.enabled === true ? (
                                            <span className="status-badge approved">
                                                ✓ Onaylandı
                                            </span>
                                        ) : (
                                            <span className="status-badge pending">
                                                ⏳ Belirsiz
                                            </span>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}

                        {currentData.length === 0 && (
                            <tr className="no-results-row">
                                <td colSpan={columns.length + (hasActions ? 2 : 1)} className="no-results">
                                    <div className="no-results-content">
                                        <span className="no-results-icon">🔍</span>
                                        <span>"{search}" için sonuç bulunamadı</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Section */}
            {totalPages > 1 && (
                <div className="pagination-container">
                    <div className="pagination-info">
                        Toplam {filtered.length} kayıt, {startIndex + 1}-{Math.min(endIndex, filtered.length)} arası gösteriliyor
                    </div>

                    <div className="pagination">
                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(1)}
                            disabled={currentPage === 1}
                            title="İlk sayfa"
                        >
                            ⏮️
                        </button>

                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            title="Önceki sayfa"
                        >
                            ◀️
                        </button>

                        {/* Sayfa numaraları */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <button
                                    key={pageNum}
                                    className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                                    onClick={() => goToPage(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            title="Sonraki sayfa"
                        >
                            ▶️
                        </button>

                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(totalPages)}
                            disabled={currentPage === totalPages}
                            title="Son sayfa"
                        >
                            ⏭️
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
